import { Component, Element, h, Host, Prop } from '@stencil/core';
import { HTMLAnchorElementTarget } from '../../utils/helpers';

@Component({
  tag: 'vds-menu-mobile-navitem',
  styleUrl: 'vds-menu-mobile-navitem.scss',
  shadow: true,
})
export class VdsMenuMobileNavItem {

  @Element() el!: HTMLVdsMenuMobileNavitemElement;

  /**
   * Nav-Item label
   */
  @Prop() label: string;

  /**
   * Sets the nav-item style (meta = smaller and light-grey)
   */
  @Prop() mode: 'default' | 'meta' = 'default';

  /**
   * Expands the nav-item list when set to true
   */
  @Prop({ mutable: true, reflect: true }) expanded: boolean = false;

  /**
   * Controls the active color of the nav-item
   */
  @Prop({ mutable: true, reflect: true }) active: boolean = false;

  /**
   * Controls the visibility of the dropdown chevron
   */
  @Prop() dropdown: boolean = false;

  /**
   * Where to display the linked URL,
   * as the name for a browsing context (a tab, window, or iframe).
   */
  @Prop() target: HTMLAnchorElementTarget = '_self';

  /**
   * Link href of the nav-item (optional)
   */
  @Prop() href?: string;

  render() {
    const { mode, expanded, href, label, dropdown, active } = this;

    if (mode == 'meta') {
      return this.renderMeta(label, href, dropdown, expanded, active);
    } else {
      return this.renderDefault(label, href, dropdown, expanded, active);
    }
  }

  private renderDefault(label: string, href: string, dropdown: boolean, expanded: boolean, active: boolean) {
    return (
      <Host>
        <li aria-expanded={expanded} class="overflow-hidden">
          <span class="w-full grid grid-cols-2 justify-items-stretch cursor-pointer py-[10px]">
            <a href={href} class={{ 'justify-self-start font-bold': true, 'text-berry': active }}>
              {label}
            </a>
            <i
              class={{
                'flex w-[34px] h-[32px] pl-[10px] py-[5px] justify-self-end': true,
                'border-l-2 border-grey-lighter': dropdown,
                'hidden': !dropdown
              }}
              onClick={() => this.expandList()}
            >
              {this.renderExpandIconSVG(expanded)}
            </i>
          </span>
          <div
            class={{
              'subitems-container grid text-[12px] pl-[15px] border-l-2 border-grey-lighter transition-all duration-[.2s] gap-y-2': true,
              'max-h-0': !expanded
            }}
          >
            <slot name="item"></slot>
          </div>
        </li>
      </Host>
    );
  }

  private renderMeta(label: string, href: string, dropdown: boolean, expanded: boolean, active: boolean) {
    return (
      <Host>
        <li aria-expanded={expanded} class="overflow-hidden">

          <span class="grid grid-cols-5 cursor-pointer" onClick={() => this.expandList()}>
            <div class="col-span-4 flex items-center">
              <a href={href} class="flex items-center w-full">
                <div class="flex items-start w-full">
                  <span class="mr-[10px] mt-[5px] flex-none leading-[0] overflow-hidden basis-[19px] md:basis-[28px] h-[19px] md:h-[28px] text-berry">
                    <slot name="icon"></slot>
                  </span>
                  <span class={{
                    'w-full text-[14px] leading-[26px]': true,
                    'text-berry': active || expanded,
                    'text-grey-light': !active && !expanded
                    }}>{label}</span>
                </div>
              </a>
            </div>
            { dropdown && (
              <i class="col-span-1 flex w-[28px] h-[30px] pl-[10px] pt-1">{this.renderExpandIconSVG(expanded)}</i>
            )}
          </span>

          <div class={{
            'subitems-container grid text-[12px] pl-[15px] border-l-2 border-grey-lighter transition-all duration-[.2s] gap-y-4': true,
            'max-h-0': !expanded
          }}>
            <slot name="item"></slot>
          </div>

        </li>
      </Host>
    );
  }

  private getSubitemsContainerHeight(): number {
    const gap = this.mode == 'meta' ? 20 : 10;
    const items = this.el.querySelectorAll('[slot=item]');
    const height = Array.from(items)
      .map(x => x.clientHeight)
      .reduce((a,b) => a + b);

    return height + ((items.length - 1) * gap);
  }

  private expandList(): void {
    const navItems = this.el.parentNode.querySelectorAll("vds-menu-mobile-navitem");
    const subitemsContainer = this.el.shadowRoot.querySelector('.subitems-container') as HTMLElement;

    this.closeOtherNavItems(navItems);

    this.expanded = !this.expanded;

    if(this.expanded) {
      subitemsContainer.style.maxHeight = this.getSubitemsContainerHeight() + "px";
    }
    else {
      subitemsContainer.style.maxHeight = "0";
    }
  }

  private closeOtherNavItems(navItems: NodeListOf<HTMLVdsMenuMobileNavitemElement>): void {
    navItems.forEach((item) => {
      const subitemsContainer = item.shadowRoot.querySelector('.subitems-container') as HTMLElement;
      if (item.expanded == true && item != this.el) {
        item.expanded = false;
        subitemsContainer.style.maxHeight = "0";
      }
    });
  }

  private renderExpandIconSVG(expanded: boolean): string {
    return (
      <svg
        x="0px"
        y="0px"
        viewBox="0 0 29 14"
        enable-background="new 0 0 29 14"
        xmlSpace="preserve"
        class={{
          'transition-transform': true,
          'rotate-180': expanded,
        }}
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-stroke-linejoin="round"
          d="M1,1l10.5,10.5c1.3,1.7,3.7,1.9,5.4,0.6c0.2-0.2,0.4-0.4,0.6-0.6L28,1"
        />
      </svg>
    );
  }
}
